import { SmallDetailsPayload } from '@/typings';
import axios from 'axios';
import { format } from 'date-fns';

export const isValidExtension =
  (allowedExtensions: string) =>
  (ext: string): boolean =>
    allowedExtensions.includes(ext);

export const isLowerThanMib =
  (maxSize: number) =>
  (size: number): boolean =>
    size / 1024 / 1024 < maxSize;

export const isPercentageValue = (value: string | number): boolean =>
  typeof value === 'string' && value.indexOf('%') > -1;

export const mm2px = (mmValue: number, dpi: number): number => {
  return mmValue * (dpi / 25.4);
};

export const px2mm = (pxValue: number, dpi: number): number => {
  return pxValue / (dpi / 25.4);
};

export type ShapeCoordsWithSize = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 *
 * @param ctx Canvas context
 * @param correctionFactor Amount of pixels you want to allow to be outside of the canvas edges
 * @param current Object containing current x and y values
 * @param shape Shape that we are currently manipulating by changing it's position or size
 * @returns {object} Object containing adjusted x and y values to be applied onto the shape in question
 */
export const limitToContextWithCorrectionFactor = (
  ctx: ShapeCoordsWithSize,
  correctionFactor = 21,
  current: { x: number; y: number },
  shape: ShapeCoordsWithSize
): { x: number; y: number } => {
  const limit =
    /**
     *
     * @param limitValue Defines constraint points for each axis
     * @param direction Differentiate between lower and upper boundaries
     * @param offset Amount of pixels to account for - on each constraint
     * @returns
     */


      (limitValue: number, direction: '<' | '>', offset: number) =>
      (currentValue: number) => {
        const valueCheck =
          direction === '>'
            ? limitValue - offset - (currentValue - correctionFactor)
            : currentValue + correctionFactor - limitValue;
        return valueCheck >= 0
          ? currentValue
          : direction === '<'
          ? limitValue - correctionFactor
          : limitValue + correctionFactor - offset;
      };

  /**
   * Leftmost value allowed
   */
  const lowerX = limit(ctx.x, '<', 0);
  /**
   * Rightmost value allowed
   */
  const upperX = limit(ctx.width, '>', shape.width);
  /**
   * Bottommost value allowed
   */
  const lowerY = limit(ctx.y, '<', 0);
  /**
   * Upermost value allowed
   */
  const upperY = limit(ctx.height, '>', shape.height);

  /**
   * @typedef {Object} Point
   * @property {number} x- The x coordinate
   * @property {number} y- The y coordinate
   *
   * @returns {Point} - x and y values limited by the Left, Right, Down, Up bounds defined above
   */
  const constrained = {
    x: lowerX(upperX(current.x)),
    y: lowerY(upperY(current.y)),
  };
  return {
    ...current,
    x: constrained.x,
    y: constrained.y,
  };
};

export const colorArrayToColorRGBA = (
  colorArray: [number, number, number, number?]
): [number?, number?, number?, number?] => {
  const [r, g, b, a] = colorArray.map((v, idx) => {
    return idx !== 3 ? v && v * 255 : v;
  });
  return [r, g, b, a];
};

/**
 * Calculates the proper height of an image with a custom width, preserving the original aspect ratio.
 *
 * @param originalHeight
 * @param originalWidth
 * @param newWidth
 */
export function determineNewHeight(
  originalHeight: number,
  originalWidth: number,
  newWidth: number
): number {
  return (originalHeight / originalWidth) * newWidth;
}

/**
 * Calculates the proper width of an image with a custom height, preserving the original aspect ratio.
 *
 * @param originalHeight
 * @param originalWidth
 * @param newHeight
 */
export function determineNewWidth(
  originalHeight: number,
  originalWidth: number,
  newHeight: number
): number {
  return (originalWidth / originalHeight) * newHeight;
}

export function determineNewWidthMm(
  originalWidthMm: number,
  originalHeightMm: number,
  newHeightMm: number
): number {
  return (originalWidthMm * newHeightMm) / originalHeightMm;
}

export function determineNewHeightMm(
  originalWidthMm: number,
  originalHeightMm: number,
  newWidthMm: number
): number {
  return (originalHeightMm * newWidthMm) / originalWidthMm;
}

export function determineNewWidthPx(
  originalWidthPx: number,
  originalWidthMm: number,
  newWidthMm: number
): number {
  return (originalWidthPx * newWidthMm) / originalWidthMm;
}

export function determineNewHeightPx(
  originalHeightPx: number,
  originalHeightMm: number,
  newHeightMm: number
): number {
  return (originalHeightPx * newHeightMm) / originalHeightMm;
}

/**
 * Calculates new value for DPI when artwork is resized.
 *
 * @param newValue
 * @param originalDPI
 * @param originalValue
 */
export function recalculateDPI(
  newValue: number,
  originalDPI: number,
  originalValue: number
): number {
  const scaleFactor = newValue / originalValue;
  return originalDPI / scaleFactor;
}

export function decodeHTMLEntities(text: string) {
  const entities = [
    ['amp', '&'],
    ['apos', "'"],
    ['#x27', "'"],
    ['#x2F', '/'],
    ['#39', "'"],
    ['#47', '/'],
    ['lt', '<'],
    ['gt', '>'],
    ['nbsp', ' '],
    ['quot', '"'],
  ];

  entities.forEach((entity) => {
    text = text?.replace(new RegExp('&' + entity[0] + ';', 'g'), entity[1]);
  });

  return text;
}

export async function convertToBlob(fileUrl: string): Promise<Blob> {
  const res = await axios({
    url: fileUrl,
    method: 'GET',
    responseType: 'blob',
  });
  return res.data;
}

export function getUtcDateTime(dateString: string): Date {
  return new Date(
    format(new Date(dateString.slice(0, -1)), 'yyyy-MM-dd HH:mm:ss')
  );
}

export function getIntershopRedirectionUrl(
  personalizationId: string,
  isConcept: boolean,
  data: string,
  iv: string
): string {
  const type = isConcept ? 'concept' : 'cart';
  return `${process.env.VUE_APP_INTERSHOP_BASE_URL}/INTERSHOP/web/WFS/midocean-BLX-Site/nl_NL/-/EUR/ViewWeb2Print-AddFromExternalSource?IV=${iv}&Data=${data}&SelectedFlow=Wizard&PersonalizationId=${personalizationId}&Type=${type}`;
}
